import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  EyeIcon,
  EyeSlashIcon,
  LinkIcon,
  PlusIcon,
  SignalIcon,
  TrashIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { integrationService } from '../services/integrations';
import { i18nService } from '../services/i18n';
import type {
  IntegrationAccessPolicy,
  IntegrationAuthType,
  IntegrationEndpoint,
  IntegrationProject,
  IntegrationProjectInput,
} from '../types/integration';

type ProjectDraft = {
  name: string;
  baseUrl: string;
  openApiUrl: string;
  authType: IntegrationAuthType;
  authHeaderName: string;
  apiKey: string;
  enabled: boolean;
};

const EMPTY_DRAFT: ProjectDraft = {
  name: '',
  baseUrl: '',
  openApiUrl: '',
  authType: 'api_key',
  authHeaderName: 'X-API-Key',
  apiKey: '',
  enabled: true,
};

const METHOD_CLASSES: Record<string, string> = {
  GET: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  POST: 'border-blue-500/40 bg-blue-500/10 text-blue-600 dark:text-blue-400',
  PUT: 'border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400',
  PATCH: 'border-violet-500/40 bg-violet-500/10 text-violet-600 dark:text-violet-400',
  DELETE: 'border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-400',
};

function projectToDraft(project: IntegrationProject): ProjectDraft {
  return {
    name: project.name,
    baseUrl: project.baseUrl,
    openApiUrl: project.openApiUrl || '',
    authType: project.authType,
    authHeaderName: project.authHeaderName || 'X-API-Key',
    apiKey: '',
    enabled: project.enabled,
  };
}

function policyLabel(policy: IntegrationAccessPolicy): string {
  const labels: Record<IntegrationAccessPolicy, string> = {
    disabled: i18nService.t('integrationPolicyDisabled'),
    manual: i18nService.t('integrationPolicyManual'),
    agent_read: i18nService.t('integrationPolicyAgentRead'),
    agent_write: i18nService.t('integrationPolicyAgentWrite'),
    automatic: i18nService.t('integrationPolicyAutomatic'),
  };
  return labels[policy];
}

const ProjectConnectionsSettings: React.FC = () => {
  const [projects, setProjects] = useState<IntegrationProject[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<ProjectDraft>(EMPTY_DRAFT);
  const [endpoints, setEndpoints] = useState<IntegrationEndpoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [methodFilter, setMethodFilter] = useState('ALL');

  const selectedProject = projects.find((project) => project.id === selectedId) || null;
  const isNewProject = selectedId === null;

  const upsertProject = useCallback((project: IntegrationProject) => {
    setProjects((current) => {
      const found = current.some((item) => item.id === project.id);
      return found
        ? current.map((item) => item.id === project.id ? project : item)
        : [project, ...current];
    });
  }, []);

  const selectProject = useCallback(async (project: IntegrationProject) => {
    setSelectedId(project.id);
    setDraft(projectToDraft(project));
    setMessage(null);
    setLoading(true);
    try {
      const result = await integrationService.getProject(project.id);
      setEndpoints(result.endpoints);
      upsertProject(result.project);
    } catch (error) {
      setEndpoints([]);
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : i18nService.t('integrationLoadFailed'),
      });
    } finally {
      setLoading(false);
    }
  }, [upsertProject]);

  const loadProjects = useCallback(async () => {
    setLoading(true);
    try {
      const loaded = await integrationService.listProjects();
      setProjects(loaded);
      if (loaded.length > 0) {
        await selectProject(loaded[0]);
      } else {
        setSelectedId(null);
        setDraft(EMPTY_DRAFT);
        setEndpoints([]);
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : i18nService.t('integrationLoadFailed'),
      });
    } finally {
      setLoading(false);
    }
  }, [selectProject]);

  useEffect(() => {
    void loadProjects();
  }, [loadProjects]);

  const startNewProject = () => {
    setSelectedId(null);
    setDraft(EMPTY_DRAFT);
    setEndpoints([]);
    setMessage(null);
    setShowApiKey(false);
  };

  const persistProject = async (): Promise<IntegrationProject> => {
    if (!draft.name.trim()) throw new Error(i18nService.t('integrationNameRequired'));
    if (!draft.baseUrl.trim()) throw new Error(i18nService.t('integrationBaseUrlRequired'));
    const input: IntegrationProjectInput = {
      name: draft.name.trim(),
      baseUrl: draft.baseUrl.trim(),
      openApiUrl: draft.openApiUrl.trim(),
      authType: draft.authType,
      authHeaderName: draft.authHeaderName.trim() || 'X-API-Key',
      enabled: draft.enabled,
      ...(draft.authType === 'none'
        ? { apiKey: '' }
        : draft.apiKey
          ? { apiKey: draft.apiKey }
          : {}),
    };
    const project = isNewProject
      ? await integrationService.createProject(input)
      : await integrationService.updateProject(selectedId!, input);
    upsertProject(project);
    setSelectedId(project.id);
    setDraft({ ...projectToDraft(project), apiKey: '' });
    return project;
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await persistProject();
      setMessage({ type: 'success', text: i18nService.t('integrationSaved') });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : i18nService.t('integrationSaveFailed'),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setMessage(null);
    try {
      const project = await persistProject();
      const result = await integrationService.testConnection(project.id);
      const detail = `${result.message} ${result.operationCount} ${i18nService.t('integrationOperations')}, ${result.latencyMs} ms`;
      setMessage({ type: result.success ? 'success' : 'error', text: detail });
      const loaded = await integrationService.getProject(project.id);
      upsertProject(loaded.project);
      setEndpoints(loaded.endpoints);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : i18nService.t('integrationTestFailed'),
      });
      if (selectedId) {
        const loaded = await integrationService.getProject(selectedId).catch(() => null);
        if (loaded) upsertProject(loaded.project);
      }
    } finally {
      setTesting(false);
    }
  };

  const handleDiscover = async () => {
    setDiscovering(true);
    setMessage(null);
    try {
      const project = await persistProject();
      const discovered = await integrationService.discover(project.id);
      setEndpoints(discovered);
      const loaded = await integrationService.getProject(project.id);
      upsertProject(loaded.project);
      setMessage({
        type: 'success',
        text: `${i18nService.t('integrationDiscoverySuccess')} ${discovered.length} ${i18nService.t('integrationOperations')}`,
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : i18nService.t('integrationDiscoveryFailed'),
      });
    } finally {
      setDiscovering(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedId || !window.confirm(i18nService.t('integrationDeleteConfirm'))) return;
    try {
      await integrationService.deleteProject(selectedId);
      const remaining = projects.filter((project) => project.id !== selectedId);
      setProjects(remaining);
      if (remaining.length > 0) {
        await selectProject(remaining[0]);
      } else {
        startNewProject();
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : i18nService.t('integrationDeleteFailed'),
      });
    }
  };

  const handleEndpointPolicy = async (
    endpoint: IntegrationEndpoint,
    accessPolicy: IntegrationAccessPolicy,
  ) => {
    try {
      const updated = await integrationService.updateEndpoint(
        endpoint.projectId,
        endpoint.id,
        {
          accessPolicy,
          enabled: accessPolicy !== 'disabled',
        },
      );
      setEndpoints((current) => current.map((item) => item.id === updated.id ? updated : item));
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : i18nService.t('integrationEndpointUpdateFailed'),
      });
    }
  };

  const handleAuthorizeReadEndpoints = async () => {
    const readEndpoints = endpoints.filter((endpoint) =>
      endpoint.riskLevel === 'read'
      && (!endpoint.enabled || endpoint.accessPolicy !== 'agent_read'),
    );
    if (readEndpoints.length === 0) return;
    setBulkUpdating(true);
    setMessage(null);
    try {
      const updated = await Promise.all(
        readEndpoints.map((endpoint) => integrationService.updateEndpoint(
          endpoint.projectId,
          endpoint.id,
          { accessPolicy: 'agent_read', enabled: true },
        )),
      );
      const updatedById = new Map(updated.map((endpoint) => [endpoint.id, endpoint]));
      setEndpoints((current) => current.map((endpoint) => updatedById.get(endpoint.id) || endpoint));
      setMessage({
        type: 'success',
        text: `${i18nService.t('integrationReadAuthorizationSuccess')} ${updated.length} ${i18nService.t('integrationOperations')}`,
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : i18nService.t('integrationReadAuthorizationFailed'),
      });
    } finally {
      setBulkUpdating(false);
    }
  };

  const filteredEndpoints = useMemo(() => {
    if (methodFilter === 'ALL') return endpoints;
    return endpoints.filter((endpoint) => endpoint.method === methodFilter);
  }, [endpoints, methodFilter]);
  const aiToolCount = endpoints.filter((endpoint) =>
    endpoint.enabled
    && ['agent_read', 'agent_write', 'automatic'].includes(endpoint.accessPolicy),
  ).length;
  const pendingReadEndpointCount = endpoints.filter((endpoint) =>
    endpoint.riskLevel === 'read'
    && (!endpoint.enabled || endpoint.accessPolicy !== 'agent_read'),
  ).length;

  return (
    <div className="flex min-h-[610px] -m-6 overflow-x-hidden">
      <aside className="w-[280px] shrink-0 border-r dark:border-claude-darkBorder border-claude-border p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold dark:text-claude-darkText text-claude-text">
            {i18nService.t('integrationProjects')}
          </h3>
          <button
            type="button"
            onClick={startNewProject}
            className="inline-flex items-center text-xs text-claude-accent hover:text-claude-accentHover"
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            {i18nService.t('integrationAddProject')}
          </button>
        </div>

        <div className="space-y-2 max-h-[535px] overflow-y-auto">
          {projects.map((project) => (
            <button
              key={project.id}
              type="button"
              onClick={() => { void selectProject(project); }}
              className={`w-full rounded-xl border p-3 text-left transition-colors ${
                selectedId === project.id
                  ? 'border-claude-accent bg-claude-accent/10'
                  : 'dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurface/40 bg-claude-surface/50 hover:border-claude-accent/50'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-sm font-medium dark:text-claude-darkText text-claude-text">
                  {project.name}
                </span>
                <span
                  className={`h-2 w-2 shrink-0 rounded-full ${
                    project.connectionStatus === 'connected'
                      ? 'bg-emerald-500'
                      : project.connectionStatus === 'failed'
                        ? 'bg-red-500'
                        : 'bg-gray-400'
                  }`}
                />
              </div>
              <p className="mt-1 truncate text-[11px] dark:text-claude-darkTextSecondary text-claude-textSecondary">
                {project.baseUrl}
              </p>
              <p className="mt-1 text-[10px] dark:text-claude-darkTextSecondary text-claude-textSecondary">
                {project.operationCount} {i18nService.t('integrationOperations')}
              </p>
            </button>
          ))}
          {!loading && projects.length === 0 && (
            <div className="rounded-xl border border-dashed dark:border-claude-darkBorder border-claude-border p-4 text-center text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary">
              {i18nService.t('integrationNoProjects')}
            </div>
          )}
        </div>
      </aside>

      <section className="flex-1 min-w-0 p-5 overflow-x-hidden overflow-y-auto max-h-[680px]">
        <div className="border-b dark:border-claude-darkBorder border-claude-border pb-4">
          <div className="flex min-w-0 items-center justify-between gap-4">
            <div className="flex min-w-0 items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-claude-accent/10 text-claude-accent">
                <LinkIcon className="h-5 w-5" />
              </span>
              <h3 className="text-base font-semibold dark:text-claude-darkText text-claude-text">
                {isNewProject ? i18nService.t('integrationNewProject') : draft.name}
              </h3>
            </div>
            <button
              type="button"
              onClick={() => setDraft((current) => ({ ...current, enabled: !current.enabled }))}
              aria-pressed={draft.enabled}
              className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                draft.enabled
                  ? 'border-emerald-500/40 bg-emerald-500/15 text-emerald-600 hover:bg-emerald-500/20 dark:text-emerald-400'
                  : 'border-red-500/40 bg-red-500/15 text-red-600 hover:bg-red-500/20 dark:text-red-400'
              }`}
            >
              {draft.enabled
                ? <CheckCircleIcon className="h-4 w-4" />
                : <XCircleIcon className="h-4 w-4" />}
              {draft.enabled
                ? i18nService.t('integrationConnectionEnabled')
                : i18nService.t('integrationConnectionDisabled')}
            </button>
          </div>
          <p className="mt-2 pl-12 text-xs leading-5 dark:text-claude-darkTextSecondary text-claude-textSecondary">
            {i18nService.t('integrationDescription')}
          </p>
        </div>

        {message && (
          <div className={`mt-3 flex items-start gap-2 rounded-xl border px-3 py-2 text-xs ${
            message.type === 'success'
              ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
              : 'border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300'
          }`}>
            {message.type === 'success'
              ? <CheckCircleIcon className="h-4 w-4 shrink-0" />
              : <XCircleIcon className="h-4 w-4 shrink-0" />}
            <span className="break-all">{message.text}</span>
          </div>
        )}

        <div className="mt-4 grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium mb-1 dark:text-claude-darkText text-claude-text">
              {i18nService.t('integrationProjectName')}
            </label>
            <input
              value={draft.name}
              onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))}
              className="input-base w-full"
              placeholder="Paws & Claws ERP"
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1 dark:text-claude-darkText text-claude-text">
              {i18nService.t('integrationAuthType')}
            </label>
            <select
              value={draft.authType}
              onChange={(event) => setDraft((current) => ({
                ...current,
                authType: event.target.value as IntegrationAuthType,
              }))}
              className="input-base w-full"
            >
              <option value="api_key">API Key Header</option>
              <option value="bearer">Bearer Token</option>
              <option value="none">{i18nService.t('integrationAuthNone')}</option>
            </select>
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium mb-1 dark:text-claude-darkText text-claude-text">
              API Base URL
            </label>
            <input
              value={draft.baseUrl}
              onChange={(event) => setDraft((current) => ({ ...current, baseUrl: event.target.value }))}
              className="input-base w-full font-mono"
              placeholder="http://localhost:3100/api/v1/"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium mb-1 dark:text-claude-darkText text-claude-text">
              OpenAPI URL <span className="font-normal opacity-60">({i18nService.t('optional')})</span>
            </label>
            <input
              value={draft.openApiUrl}
              onChange={(event) => setDraft((current) => ({ ...current, openApiUrl: event.target.value }))}
              className="input-base w-full font-mono"
              placeholder="http://localhost:3100/openapi.json"
            />
          </div>
          {draft.authType === 'api_key' && (
            <div>
              <label className="block text-xs font-medium mb-1 dark:text-claude-darkText text-claude-text">
                {i18nService.t('integrationHeaderName')}
              </label>
              <input
                value={draft.authHeaderName}
                onChange={(event) => setDraft((current) => ({ ...current, authHeaderName: event.target.value }))}
                className="input-base w-full font-mono"
                placeholder="X-API-Key"
              />
            </div>
          )}
          {draft.authType !== 'none' && (
            <div className={draft.authType === 'bearer' ? 'col-span-2' : ''}>
              <label className="block text-xs font-medium mb-1 dark:text-claude-darkText text-claude-text">
                API Key
              </label>
              <div className="relative">
                <input
                  type={showApiKey ? 'text' : 'password'}
                  value={draft.apiKey}
                  onChange={(event) => setDraft((current) => ({ ...current, apiKey: event.target.value }))}
                  className="input-base w-full pr-9 font-mono"
                  placeholder={selectedProject?.apiKeyConfigured
                    ? i18nService.t('integrationSecretUnchanged')
                    : 'pc_live_...'}
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey((value) => !value)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 dark:text-claude-darkTextSecondary text-claude-textSecondary"
                >
                  {showApiKey ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => { void handleSave(); }}
            disabled={saving}
            className="rounded-xl bg-claude-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-claude-accentHover disabled:opacity-50"
          >
            {saving ? i18nService.t('saving') : i18nService.t('save')}
          </button>
          <button
            type="button"
            onClick={() => { void handleTest(); }}
            disabled={testing}
            className="inline-flex items-center rounded-xl border dark:border-claude-darkBorder border-claude-border px-3 py-1.5 text-xs font-medium dark:text-claude-darkText text-claude-text disabled:opacity-50"
          >
            <SignalIcon className="h-4 w-4 mr-1.5" />
            {testing ? i18nService.t('testing') : i18nService.t('testConnection')}
          </button>
          <button
            type="button"
            onClick={() => { void handleDiscover(); }}
            disabled={discovering}
            className="inline-flex items-center rounded-xl border dark:border-claude-darkBorder border-claude-border px-3 py-1.5 text-xs font-medium dark:text-claude-darkText text-claude-text disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-4 w-4 mr-1.5 ${discovering ? 'animate-spin' : ''}`} />
            {i18nService.t('integrationDiscover')}
          </button>
          {!isNewProject && (
            <button
              type="button"
              onClick={() => { void handleDelete(); }}
              className="ml-auto inline-flex items-center rounded-xl border border-red-500/30 px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400"
            >
              <TrashIcon className="h-4 w-4 mr-1.5" />
              {i18nService.t('delete')}
            </button>
          )}
        </div>

        <div className="mt-6 border-t dark:border-claude-darkBorder border-claude-border pt-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h4 className="text-sm font-semibold dark:text-claude-darkText text-claude-text">
                {i18nService.t('integrationApiDirectory')}
              </h4>
              <p className="text-[11px] dark:text-claude-darkTextSecondary text-claude-textSecondary">
                {endpoints.length} {i18nService.t('integrationOperations')}
                {' · '}
                {aiToolCount} {i18nService.t('integrationAiToolsAuthorized')}
              </p>
              {selectedProject?.resolvedOpenApiUrl && (
                <p className="mt-0.5 max-w-[320px] break-all text-[10px] dark:text-claude-darkTextSecondary text-claude-textSecondary">
                  {selectedProject.resolvedOpenApiUrl}
                </p>
              )}
            </div>
            <div className="shrink-0">
              <select
                value={methodFilter}
                onChange={(event) => setMethodFilter(event.target.value)}
                className="input-base !w-28 !py-1 text-xs"
                aria-label="HTTP method filter"
              >
                <option value="ALL">ALL</option>
                {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((method) => (
                  <option key={method} value={method}>{method}</option>
                ))}
              </select>
            </div>
          </div>

          {endpoints.length > 0 && (
            <div className={`mt-3 flex items-center justify-between gap-3 rounded-xl border px-3 py-2.5 ${
              aiToolCount > 0
                ? 'border-emerald-500/25 bg-emerald-500/5'
                : 'border-amber-500/30 bg-amber-500/10'
            }`}>
              <div className="min-w-0">
                <p className={`text-xs font-medium ${
                  aiToolCount > 0
                    ? 'text-emerald-700 dark:text-emerald-300'
                    : 'text-amber-700 dark:text-amber-300'
                }`}>
                  {aiToolCount > 0
                    ? `${aiToolCount} ${i18nService.t('integrationAiToolsAuthorized')}`
                    : i18nService.t('integrationNoAiTools')}
                </p>
                <p className="mt-0.5 text-[10px] leading-4 dark:text-claude-darkTextSecondary text-claude-textSecondary">
                  {i18nService.t('integrationAiAuthorizationHint')}
                </p>
              </div>
              {pendingReadEndpointCount > 0 && (
                <button
                  type="button"
                  onClick={() => { void handleAuthorizeReadEndpoints(); }}
                  disabled={bulkUpdating}
                  className="shrink-0 rounded-lg border border-claude-accent/40 bg-claude-accent/10 px-2.5 py-1.5 text-[11px] font-medium text-claude-accent hover:bg-claude-accent/15 disabled:opacity-50"
                >
                  {bulkUpdating
                    ? i18nService.t('integrationAuthorizing')
                    : i18nService.t('integrationAuthorizeAllReads')}
                </button>
              )}
            </div>
          )}

          <div className="mt-3 space-y-2">
            {filteredEndpoints.map((endpoint) => (
              <div
                key={endpoint.id}
                className="rounded-xl border dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurface/40 bg-claude-surface/50 p-3"
              >
                <div className="flex items-center gap-3">
                  <span className={`min-w-[62px] rounded-lg border px-2 py-1 text-center text-xs font-bold ${
                    METHOD_CLASSES[endpoint.method] || 'border-gray-400/40 text-gray-500'
                  }`}>
                    {endpoint.method}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-mono text-xs font-medium dark:text-claude-darkText text-claude-text">
                      {endpoint.displayPath}
                    </p>
                    <p className="mt-0.5 truncate text-[11px] dark:text-claude-darkTextSecondary text-claude-textSecondary">
                      {endpoint.summary || endpoint.operationId}
                      {endpoint.parameters.length > 0 ? ` · ${endpoint.parameters.length} params` : ''}
                      {endpoint.requestBody ? ' · request body' : ''}
                    </p>
                  </div>
                  <select
                    value={endpoint.accessPolicy}
                    onChange={(event) => {
                      void handleEndpointPolicy(
                        endpoint,
                        event.target.value as IntegrationAccessPolicy,
                      );
                    }}
                    className="input-base !w-40 !py-1 text-xs"
                  >
                    {(['disabled', 'manual', 'agent_read', 'agent_write', 'automatic'] as IntegrationAccessPolicy[])
                      .filter((policy) => endpoint.riskLevel === 'write'
                        ? policy !== 'agent_read'
                        : !['agent_write', 'automatic'].includes(policy))
                      .map((policy) => (
                        <option key={policy} value={policy}>{policyLabel(policy)}</option>
                      ))}
                  </select>
                </div>
              </div>
            ))}
            {!loading && endpoints.length === 0 && (
              <div className="rounded-xl border border-dashed dark:border-claude-darkBorder border-claude-border py-8 text-center text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary">
                {i18nService.t('integrationNoEndpoints')}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProjectConnectionsSettings;
